import React, { useState, useEffect, useCallback } from "react";
import {
  Button, Box, Chip, TextField, Typography, InputAdornment,
  Dialog, Checkbox, Tooltip
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import IndeterminateCheckBoxRoundedIcon from "@mui/icons-material/IndeterminateCheckBoxRounded";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV, filterRows } from "../../utils/tableUtils";

const fallback = [
  { _id: "1", grade: "Nilai 1", subjectName: "Tamil", textbookTitle: "Tamil Mozhi Payirchi - Level 1", documentType: "Textbook" },
  { _id: "2", grade: "Nilai 2", subjectName: "Tamil", textbookTitle: "Tamil Mozhi Payirchi - Level 2", documentType: "Textbook" },
  { _id: "3", grade: "Nilai 3", subjectName: "Tamil", textbookTitle: "Tamil Ilakkanam - Level 3", documentType: "Textbook" },
  { _id: "4", grade: "Nilai 4", subjectName: "Tamil", textbookTitle: "Tamil Padippu - Level 4", documentType: "Textbook" },
  { _id: "5", grade: "Nilai 5", subjectName: "Tamil", textbookTitle: "Tamil Mozhi Payirchi - Level 5", documentType: "Workbook" },
  { _id: "6", grade: "Nilai 6", subjectName: "Tamil", textbookTitle: "Tamil Ilakkanam - Level 6", documentType: "Textbook" },
  { _id: "7", grade: "Nilai 7", subjectName: "Tamil", textbookTitle: "Tamil Padippu - Level 7", documentType: "Textbook" },
  { _id: "8", grade: "Nilai 8", subjectName: "Tamil", textbookTitle: "Tamil Mozhi Payirchi - Level 8", documentType: "Workbook" },
];

const exportColumns = [
  { label: "Grade", field: "grade" },
  { label: "Subject Name", field: "subjectName" },
  { label: "Textbook Title", field: "textbookTitle" },
  { label: "Document Type", field: "documentType" },
];

const Textbooks = () => {
  const [rawData, setRawData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadTextbooks = useCallback(async () => {
    try {
      const response = await fetchData("student/textbook/list");
      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        const mapped = data.map((r, i) => ({
          _id: String(r.id || i + 1),
          grade: r.grade,
          subjectName: r.subjectName,
          textbookTitle: r.title || r.textbookTitle,
          documentType: r.documentType || r.type || "Textbook",
          fileUrl: r.fileUrl || r.url,
        }));
        setRawData(mapped.length > 0 ? mapped : fallback);
      } else {
        setRawData(fallback);
      }
    } catch (_) {
      setRawData(fallback);
    }
  }, []);

  useEffect(() => { loadTextbooks(); }, [loadTextbooks]);

  const handleDownload = (url) => { if (url) window.open(url, "_blank"); };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredRaw = filterRows(rawData, filterText);

  const allSelected = filteredRaw.length > 0 && filteredRaw.every((r) => selectedIds.has(r._id));
  const someSelected = !allSelected && filteredRaw.some((r) => selectedIds.has(r._id));

  const handleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredRaw.map((r) => r._id)));
  };

  const SelectAllHeader = (
    <Tooltip title={allSelected ? "Deselect all" : "Select all"}>
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#fff", fontSize: "20px" }} />}
        indeterminateIcon={<IndeterminateCheckBoxRoundedIcon sx={{ color: "#4caf50", fontSize: "20px" }} />}
        sx={{ padding: "2px" }}
      />
    </Tooltip>
  );

  const handleExport = () => exportToCSV(exportColumns, filteredRaw, "textbooks.csv");

  const handleDeleteConfirm = () => {
    setRawData([]);
    setSelectedIds(new Set());
    setDeleteConfirm(false);
    setToast({ open: true, severity: "success", message: "Records cleared from view" });
  };

  const styles = {
    container: { textAlign: "left", padding: "1rem" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
    actionBtn: { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" },
    badge: { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" },
    downloadButton: { backgroundColor: "#4caf50", color: "#fff", fontSize: "12px", textTransform: "none", padding: "6px 16px", borderRadius: "6px", "&:hover": { backgroundColor: "#388e3c" } },
  };

  const tableColumns = [
    { label: SelectAllHeader, field: "checkbox" },
    { label: "Grade", field: "grade" },
    { label: "Subject Name", field: "subjectName" },
    { label: "Textbook Title", field: "textbookTitle" },
    { label: "Document Type", field: "documentType" },
    { label: "Action", field: "action" },
  ];

  const displayRows = filteredRaw.map((row) => ({
    ...row,
    checkbox: (
      <Checkbox
        checked={selectedIds.has(row._id)}
        onChange={() => handleToggleSelect(row._id)}
        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ color: "#D5D7DA" }} />}
        checkedIcon={<CheckBoxRoundedIcon sx={{ color: "#4caf50" }} />}
        sx={{ padding: "4px" }}
      />
    ),
    action: (
      <Button sx={styles.downloadButton} startIcon={<DownloadIcon sx={{ fontSize: "14px" }} />} onClick={() => handleDownload(row.fileUrl)}>
        Download
      </Button>
    ),
  }));

  return (
    <div style={styles.container}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Clear Records</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>Remove all displayed records from this view?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteConfirm(false)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3 }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>

      <p style={{ color: "rgba(0, 0, 0, 0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Textbooks
      </p>

      <Box sx={styles.headerRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h2 style={{ margin: 0 }}>Textbooks</h2>
          <Chip label={`${filteredRaw.length} Textbooks`} sx={styles.badge} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: showFilter ? 1 : 2, flexWrap: "wrap" }}>
        <Button startIcon={<DeleteOutlineIcon />} sx={styles.actionBtn} onClick={() => setDeleteConfirm(true)}>Delete</Button>
        <Button startIcon={<FilterListIcon />} sx={{ ...styles.actionBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }} onClick={() => setShowFilter((v) => !v)}>Filters</Button>
        <Button startIcon={<FileDownloadOutlinedIcon />} sx={styles.actionBtn} onClick={handleExport}>Export</Button>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by grade, subject, title..."
            value={filterText} onChange={(e) => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      <DynamicTable columns={tableColumns} rows={displayRows} />
    </div>
  );
};

export default Textbooks;
